// RedditCommentSkeleton.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import CommentPlaceholder from './CommentPlaceholder';
import questionMarkIcon from '../../images/question_mark.svg';
import { IoChevronBack } from 'react-icons/io5';
import './RedditCommentSkeleton.css';
import OriginalPost from './OriginalPost';
import InfoScreen from '../InfoScreen';
import { logEvent } from '../../utils/Logger';
import { fetchCommentRoots } from '../../utils/apiHelpers';
import { fetchComment } from '../../utils/apiHelpers';
import { Tag as TagType } from '../../types/types';

interface Sentence  {
  text: string;
  comment_id: number;
  frame: string;
  id: number;
  is_moral_claim: boolean;
}
interface Comment {
  title: string;
  username: string;
  is_visible: boolean;
  visibility: boolean;
  frames: TagType[];
  parent_id: number | null;
  id: number;
  is_user_created: boolean;
  text: string;
  explanation: string;
  children: Comment[];
  sentences?: Sentence[];
  depth?: number;
  created_at: string;
}

interface RedditCommentSkeletonProps {
  id: string;
}

const RedditCommentSkeleton: React.FC<RedditCommentSkeletonProps> = ({ id }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: postId } = useParams<{ id: string }>();
  const [comments, setComments] = useState<Comment[]>([]);
  const [title, setTitle] = useState<string>('');
  const username = localStorage.getItem('username') || 'Guest';

  const [showInfoPopup, setShowInfoPopup] = useState(false);

  const [commentRoots, setCommentRoots] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [apiCommentData, setApiCommentData] = useState<any>(null);

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        const [roots, fetchedCommentData] = await Promise.all([
          fetchCommentRoots(),
          fetchComment(parseInt(id, 10), username)
        ]);
        
        setCommentRoots(roots);
        setApiCommentData(fetchedCommentData);

        console.log('Fetched comment data:', fetchedCommentData); // Add this line

        if (fetchedCommentData && fetchedCommentData.children) {
          setComments(fetchedCommentData.children);
          console.log('Set comments:', fetchedCommentData.children); // Add this line
        } else {
          console.log('No comments found in fetched data'); // Add this line
          setComments([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [id]);

  const handleInfoClick = () => {
    setShowInfoPopup(true);
    logEvent('info_click', 'User clicked info button', location.pathname);
  };

  const handleCloseInfoPopup = () => {
    setShowInfoPopup(false);
  };

  const handleReply = (tags: TagType[], content: string, commentId: number, sentences: Sentence[], title: string ) => {
    const payload = {
      comment_id: commentId,
      content: content,
    }
    logEvent('reply_click', JSON.stringify(payload), location.pathname);
    navigate(`/reply/${commentId}`, { state: { tags, content, sentences, title } });
  };

  const handleBackClick = () => {
    logEvent('back_click', 'User clicked back button', location.pathname);
    navigate('/posts');
  };

  const highlightMatchingText = (content: string, matchText: string, tagName: string) => {
    if (!matchText || !tagName) return content;
    const regex = new RegExp(`(${matchText})`, 'gi');
    return content.replace(regex, `<span class="highlight ${tagName.toLowerCase().replace('/', '-')}-highlight">$1</span>`);
  };

  const renderComments = (comments: Comment[], depth = 0) => {
    console.log('Rendering comments:', comments); // Add this line
    return comments.map((comment) => (
      (comment.visibility) ? (<React.Fragment key={comment.id}>
        <CommentPlaceholder
          depth={depth}
          author={comment.username}
          content={comment.text}
          tags={comment.frames}
          explanation={comment.explanation}
          sentences={comment.sentences}
          commentId={comment.id}
          createdAt={comment.created_at}
          onReply={() => handleReply(comment.frames, comment.text, comment.id, comment.sentences || [], comment.title)}
        />
        {comment.children && comment.children.length > 0 && renderComments(comment.children, depth + 1)}
      </React.Fragment>) : null
    ));
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="reddit-skeleton">
      <div className="header-section">
        <div className="header">
          <div className="header-content">
            <button className="back-button" onClick={handleBackClick}>
              <IoChevronBack className="back-icon" />
            </button>
            <h2>Comments</h2>
          </div>
          <button className="info-button" onClick={handleInfoClick}>
            <img src={questionMarkIcon} alt="Info" className="info-icon" />
          </button>
        </div>
      </div>
      <div className="comment-section">
        <OriginalPost fetchedCommentData={apiCommentData} id={id} />
        {comments.length > 0 ? (
          renderComments(comments)
        ) : (
          <p>No comments available</p>
        )}
      </div>
      {showInfoPopup && (
        <div className="info-popup">
          <InfoScreen onClose={handleCloseInfoPopup} />
        </div>
      )}
    </div>
  );
};

export default RedditCommentSkeleton;